/**
 * Deriving a place that has no gauge from a station that has one, as the Delphi app does
 * (obj_LocatieInfo.pas on Ontwikkeltak_3.6). Two blends and a curve:
 *
 *  - `blendWeight` interpolates the time offset across the day, from the value given for the
 *    tide around 02:00 to the one around 08:00 and back (`SchuifTijdstip`, :596);
 *  - `springNeap` interpolates the height offset from springs to neaps over seven days
 *    (`NiveauHoger`, :606, using `DagenNaSpringtij`);
 *  - `deriveLevel` rebuilds the curve between the moved extremes as a raised cosine
 *    (`AfgeleideWaterstandVanHwLw`, :827).
 *
 * Everything works in UTC milliseconds and centimetres, like the rest of this package.
 */
const DAY = 86_400_000;
const MINUTE = 60_000;
/** Full moon on 29 November 2016 12:18 UTC, the epoch the old app counts from */
const FULL_MOON = Date.UTC(2016, 10, 29, 12, 18);
/** Half a synodic month, in days: springs follow both full and new moon */
const SPRING_PERIOD_DAYS = 14.765294;
const DEFAULT_SPRING_LAG_DAYS = 2.5;
/**
 * Where a moment sits in the spring-neap cycle: 0 at springs, ±7 at neaps, in whole days.
 * `springLagDays` is how long spring tide trails the moon at this station.
 */
export function springNeapDays(time, springLagDays = DEFAULT_SPRING_LAG_DAYS) {
    const x = (time - FULL_MOON - springLagDays * DAY) / (SPRING_PERIOD_DAYS * DAY);
    // a positive fraction of the cycle, so dates before the epoch behave like dates after it
    const fraction = x - Math.floor(x);
    const days = Math.min(Math.round(fraction * SPRING_PERIOD_DAYS), 14);
    return days > 7 ? days - 14 : days;
}
/**
 * The weight given to the 08:00/20:00 offset at a moment: 0 at 02:00 and 14:00, 1 at 08:00
 * and 20:00, triangular in between. Follows the old app in working on the UTC timestamp, so
 * the turning points are 02:00 and 08:00 UTC rather than local clock time.
 */
export function blendWeight(time) {
    const dayFraction = ((time - 2 * 3_600_000) % DAY + DAY) % DAY / DAY;
    let t = 4 * dayFraction;
    if (t > 2)
        t -= 2;
    return 1 - Math.abs(t - 1);
}
/** The time offset that applies at a moment, in minutes */
export function offsetMinutesAt(time, later2, later8) {
    return later2 + blendWeight(time) * (later8 - later2);
}
/**
 * The height offset that applies at a moment, in centimetres. Linear from springs to neaps,
 * with the same integer truncation the Delphi `div` does.
 */
export function higherAt(time, springLagDays, higherSpring, higherNeap) {
    if (higherSpring === 0 && higherNeap === 0)
        return 0;
    const days = Math.abs(springNeapDays(time, springLagDays));
    return higherSpring + Math.trunc(((higherNeap - higherSpring) * days) / 7);
}
/** One of the station's extremes, moved to where and how high it is at the derived place */
export function deriveExtremum(e, o, springLagDays) {
    const hw = e.type === 'HW';
    const later = offsetMinutesAt(e.time, hw ? o.hwLater2 : o.lwLater2, hw ? o.hwLater8 : o.lwLater8);
    const higher = higherAt(e.time, springLagDays, hw ? o.hwHigherSpring : o.lwHigherSpring, hw ? o.hwHigherNeap : o.lwHigherNeap);
    return { time: e.time + Math.round(later) * MINUTE, level: e.level + higher, type: e.type };
}
/** The station's extremes as they fall at the derived place, in order */
export function deriveExtrema(extrema, o, springLagDays) {
    return extrema.map(e => deriveExtremum(e, o, springLagDays)).sort((a, b) => a.time - b.time);
}
/**
 * The extremes of a grid of levels: every point higher (or lower) than both its neighbours.
 * Used to derive a place's measured and forecast levels from its station's, which the old
 * app does by passing the graph kind through the same derivation.
 */
export function gridExtrema(points) {
    const out = [];
    for (let i = 1; i < points.length - 1; i++) {
        const prev = points[i - 1];
        const p = points[i];
        const next = points[i + 1];
        if (p.level > prev.level && p.level >= next.level)
            out.push({ time: p.time, level: p.level, type: 'HW' });
        else if (p.level < prev.level && p.level <= next.level)
            out.push({ time: p.time, level: p.level, type: 'LW' });
    }
    return out;
}
/**
 * The level at a moment, rebuilt between the two derived extremes around it as a raised
 * cosine. Null outside the extremes given.
 *
 * The Delphi evaluates this at `time - fTijLater`, the mean of the four time offsets, even
 * though the extremes it interpolates between have already been moved. That shifts the curve
 * away from its own high and low waters -- by 18 minutes at Juist -- so the curve would peak
 * where the table says it does not. We leave that out: the curve and the extremes agree here.
 */
export function deriveLevel(derived, time) {
    if (derived.length === 0)
        return null;
    let lo = 0;
    let hi = derived.length - 1;
    if (time < derived[lo].time || time > derived[hi].time)
        return null;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (derived[mid].time <= time)
            lo = mid;
        else
            hi = mid;
    }
    const a = derived[lo];
    const b = derived[hi];
    if (b.time <= a.time + 1000)
        return a.level;
    const phase = (time - a.time) / (b.time - a.time);
    return a.level + (b.level - a.level) * (0.5 - 0.5 * Math.cos(Math.PI * phase));
}
/** The derived curve on a fixed grid, for drawing and for the depth arithmetic */
export function deriveCurve(derived, from, to, stepMs = 600_000) {
    const points = [];
    for (let t = Math.ceil(from / stepMs) * stepMs; t <= to; t += stepMs) {
        const level = deriveLevel(derived, t);
        if (level !== null)
            points.push({ time: t, level: Math.round(level) });
    }
    return points;
}
/** A place derived by moving the station's curve bodily, with no reshaping */
export function timeshiftMinutes(o) {
    return Math.round((o.hwLater2 + o.hwLater8 + o.lwLater2 + o.lwLater8) / 4);
}
export function derivationKind(sub) {
    return sub.kind;
}
export const NO_OFFSETS = {
    hwLater2: 0, hwLater8: 0, lwLater2: 0, lwLater8: 0,
    hwHigherSpring: 0, hwHigherNeap: 0, lwHigherSpring: 0, lwHigherNeap: 0
};
/**
 * Offsets along a chain add up, as obj_LocatieInfo.pas:450 walks it: a shallow behind a
 * sublocation is the sublocation's tide with the shallow's own shift on top.
 */
export function addOffsets(a, b) {
    return {
        hwLater2: a.hwLater2 + b.hwLater2,
        hwLater8: a.hwLater8 + b.hwLater8,
        lwLater2: a.lwLater2 + b.lwLater2,
        lwLater8: a.lwLater8 + b.lwLater8,
        hwHigherSpring: a.hwHigherSpring + b.hwHigherSpring,
        hwHigherNeap: a.hwHigherNeap + b.hwHigherNeap,
        lwHigherSpring: a.lwHigherSpring + b.lwHigherSpring,
        lwHigherNeap: a.lwHigherNeap + b.lwHigherNeap
    };
}
/** A shallow's own time shift, as offsets: the same value morning and evening, no heights */
export function shallowOffsets(hwOffsetMinutes, lwOffsetMinutes) {
    return {
        ...NO_OFFSETS,
        hwLater2: hwOffsetMinutes, hwLater8: hwOffsetMinutes,
        lwLater2: lwOffsetMinutes, lwLater8: lwOffsetMinutes
    };
}
/** Does anything here change a height? If not, the curve can simply be shifted in time. */
export function reshapes(o) {
    return o.hwHigherSpring !== 0 || o.hwHigherNeap !== 0 || o.lwHigherSpring !== 0 || o.lwHigherNeap !== 0 ||
        o.hwLater2 !== o.lwLater2 || o.hwLater8 !== o.lwLater8;
}
/**
 * The measured or forecast high (or low) water around a predicted one.
 *
 * The astronomy says a high water is due; the gauge and the weather forecast say what the
 * water actually did around then. Looking for the highest point within a couple of hours of
 * the prediction, rather than for every local maximum, keeps a centimetre of ripple on a
 * flat peak from reading as a tide of its own.
 *
 * A ten-minute series puts a peak in a ten-minute box, which on a slack high water is a poor
 * account of when it fell; so the three points around the winner are read as a parabola and
 * its top taken, the way a peak is picked out of any sampled signal.
 *
 * Null when the window is not covered on both sides of the winner: a series that simply
 * stops has its last point as its highest, and that is the end of the data, not a high water.
 */
export function peakAround(points, time, type, windowMs = 2 * 3600_000) {
    const sign = type === 'HW' ? 1 : -1;
    let best = -1;
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        if (Math.abs(p.time - time) > windowMs)
            continue;
        if (best < 0 || sign * p.level > sign * points[best].level)
            best = i;
    }
    if (best <= 0 || best >= points.length - 1)
        return null;
    const before = points[best - 1];
    const peak = points[best];
    const after = points[best + 1];
    // both neighbours must be the sampling interval away, or there is a gap and no peak
    const step = peak.time - before.time;
    if (step <= 0 || after.time - peak.time !== step)
        return null;
    if (sign * before.level > sign * peak.level || sign * after.level > sign * peak.level)
        return null;
    return refine(before, peak, after);
}
/** The top of a parabola through three evenly spaced points, or the middle one where there is none */
function refine(before, peak, after) {
    const step = peak.time - before.time;
    const curvature = before.level - 2 * peak.level + after.level;
    if (curvature === 0)
        return { time: peak.time, level: peak.level };
    const shift = (0.5 * (before.level - after.level)) / curvature;
    if (Math.abs(shift) > 1)
        return { time: peak.time, level: peak.level };
    return {
        time: Math.round(peak.time + shift * step),
        level: peak.level - 0.25 * (before.level - after.level) * shift
    };
}
/**
 * Every top of a high (or low) water that has more than one.
 *
 * Off Texel and Den Helder the high water often comes twice, two tops an hour or two apart and
 * within a centimetre or two of each other, with the water hardly falling between them. Which
 * of the two is "the" high water then turns on that centimetre: the old app gave the one its
 * forecast had higher, this one the one the gauge had higher, and the times were two hours
 * apart over the same water. So both are given, and the reader sees that the water stands high
 * from the first to the second.
 *
 * A top counts when it is within `within` of the highest, at least `minGapMs` from its
 * neighbour, and the water falls at least `dip` below the lower of the two in between -- less
 * than that is the ripple on a gauge, not a second tide. Sorted by time; a single top is a
 * plain high water, and an empty list means the series does not cover it.
 */
export function topsAround(points, time, type, options = {}) {
    const { windowMs = 3 * 3600_000, within = 10, dip = 3, minGapMs = 3600_000 } = options;
    const sign = type === 'HW' ? 1 : -1;
    const v = (i) => sign * points[i].level;
    // local tops inside the window, with a neighbour either side the sampling interval away;
    // on a flat run the last sample of it is the one taken
    const candidates = [];
    for (let i = 1; i < points.length - 1; i++) {
        const p = points[i];
        if (Math.abs(p.time - time) > windowMs)
            continue;
        const step = p.time - points[i - 1].time;
        if (step <= 0 || points[i + 1].time - p.time !== step)
            continue;
        if (v(i) >= v(i - 1) && v(i) > v(i + 1))
            candidates.push(i);
    }
    // tops without a real dip between them, or too close together, are one top: the higher
    const kept = [];
    for (const i of candidates) {
        const prev = kept[kept.length - 1];
        if (prev === undefined) {
            kept.push(i);
            continue;
        }
        let trough = Infinity;
        for (let j = prev; j <= i; j++)
            trough = Math.min(trough, v(j));
        const separate = Math.min(v(prev), v(i)) - trough >= dip && points[i].time - points[prev].time >= minGapMs;
        if (separate)
            kept.push(i);
        else if (v(i) > v(prev))
            kept[kept.length - 1] = i;
    }
    if (!kept.length)
        return [];
    const best = Math.max(...kept.map(v));
    return kept.filter(i => v(i) >= best - within).map(i => refine(points[i - 1], points[i], points[i + 1]));
}
//# sourceMappingURL=derive.js.map